/**
 * CalendarConnectionCard.js
 * Enterprise-level card for managing a single calendar provider (Google or Outlook).
 */
import React, { useState } from 'react';
import { Box, Typography, Button, Switch, Chip, Divider, CircularProgress, Tooltip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SyncIcon from '@mui/icons-material/Sync';
import LinkIcon from '@mui/icons-material/Link';

const GOOGLE_ICON = 'https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg';
const OUTLOOK_ICON = "data:image/svg+xml;charset=utf-8," + encodeURIComponent('<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="3" fill="#0072C6" /><text x="4" y="17" fill="white" font-size="13" font-weight="bold" font-family="Arial">O</text></svg>');

const providerConfig = {
    google: {
        icon: GOOGLE_ICON,
        name: 'Google Calendar',
        description: 'Sync appointments with Google Calendar. Auto-generate Google Meet links for every booking.',
        color: '#4285F4',
        gradient: 'linear-gradient(135deg, #4285F4 0%, #34A853 100%)',
        bgLight: '#f0f6ff',
        borderConnected: '#93c5fd',
        chipBg: '#eff6ff',
        chipText: '#1d4ed8',
    },
    outlook: {
        icon: OUTLOOK_ICON,
        name: 'Microsoft Outlook',
        description: 'Sync with Outlook Calendar. Auto-generate Microsoft Teams meeting links for every booking.',
        color: '#0078d4',
        gradient: 'linear-gradient(135deg, #0078d4 0%, #106ebe 100%)',
        bgLight: '#f0f7ff',
        borderConnected: '#7dd3fc',
        chipBg: '#f0f9ff',
        chipText: '#0369a1',
    },
};

const CalendarConnectionCard = ({
    provider,
    isConnected,
    email,
    syncEnabled,
    lastSyncedAt,
    loading = false,
    onConnect,
    onDisconnect,
    onToggleSync,
}) => {
    const [disconnecting, setDisconnecting] = useState(false);
    const cfg = providerConfig[provider] || providerConfig.google;

    const handleDisconnect = async () => {
        setDisconnecting(true);
        await onDisconnect?.();
        setDisconnecting(false);
    };

    return (
        <Box
            sx={{
                borderRadius: 3,
                border: '1.5px solid',
                borderColor: isConnected ? cfg.borderConnected : '#e2e8f0',
                bgcolor: isConnected ? cfg.bgLight : '#fafafa',
                p: 0,
                transition: 'all 0.3s ease',
                boxShadow: isConnected ? `0 6px 24px ${cfg.color}1a` : '0 2px 8px rgba(0,0,0,0.04)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Left accent bar when connected */}
            {isConnected && (
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: cfg.gradient, borderRadius: '0 0 0 0' }} />
            )}

            <Box sx={{ p: 3, pl: isConnected ? 3.5 : 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* Provider icon */}
                        <Box sx={{
                            width: 48, height: 48, borderRadius: 2.5,
                            bgcolor: isConnected ? 'white' : '#f1f5f9',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: isConnected ? `0 2px 12px ${cfg.color}22` : 'none',
                            border: `1.5px solid ${isConnected ? cfg.borderConnected : '#e2e8f0'}`,
                            transition: 'all 0.3s',
                        }}>
                            <img src={cfg.icon} width="26" alt={provider} style={{ opacity: isConnected ? 1 : 0.45 }} />
                        </Box>

                        <Box>
                            <Typography variant="subtitle1" fontWeight={800} sx={{ color: isConnected ? cfg.color : '#475569', letterSpacing: '-0.2px' }}>
                                {cfg.name}
                            </Typography>
                            {isConnected && email && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                    <CheckCircleOutlineIcon sx={{ fontSize: 12, color: '#22c55e' }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                                        {email}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Status chip */}
                    <Chip
                        label={isConnected ? 'Connected' : 'Not Connected'}
                        size="small"
                        sx={{
                            fontWeight: 700, fontSize: '0.68rem',
                            bgcolor: isConnected ? '#f0fdf4' : '#f1f5f9',
                            color: isConnected ? '#16a34a' : '#94a3b8',
                            border: `1px solid ${isConnected ? '#86efac' : '#e2e8f0'}`,
                        }}
                    />
                </Box>

                {/* Description */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.7, fontSize: '0.82rem' }}>
                    {cfg.description}
                </Typography>

                {/* Connected details */}
                {isConnected && (
                    <>
                        <Divider sx={{ mb: 2, borderColor: '#e2e8f0' }} />

                        {/* Auto-sync toggle */}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            mb: 1.5, px: 1.5, py: 1, borderRadius: 2,
                            bgcolor: 'rgba(255,255,255,0.7)', border: '1px solid #e2e8f0',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SyncIcon sx={{ fontSize: 16, color: cfg.color }} />
                                <Box>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: '#334155', fontSize: '0.82rem' }}>
                                        Auto-sync
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.70rem' }}>
                                        Automatically sync new bookings
                                    </Typography>
                                </Box>
                            </Box>
                            <Switch
                                size="small"
                                checked={syncEnabled}
                                onChange={(e) => onToggleSync?.(e.target.checked)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: cfg.color },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: cfg.color },
                                }}
                            />
                        </Box>

                        {lastSyncedAt && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontSize: '0.70rem' }}>
                                Last synced: {new Date(lastSyncedAt).toLocaleString()}
                            </Typography>
                        )}
                        <Divider sx={{ mb: 2, borderColor: '#e2e8f0' }} />
                    </>
                )}

                {/* Action button */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {!isConnected ? (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onConnect}
                            disabled={loading}
                            startIcon={
                                loading
                                    ? <CircularProgress size={14} color="inherit" />
                                    : <img src={cfg.icon} width="14" alt="" />
                            }
                            sx={{
                                background: cfg.gradient,
                                borderRadius: 2, textTransform: 'none',
                                fontWeight: 700, px: 2.5,
                                boxShadow: `0 4px 14px ${cfg.color}44`,
                                '&:hover': { boxShadow: `0 6px 20px ${cfg.color}66`, transform: 'translateY(-1px)' },
                                transition: 'all 0.2s',
                            }}
                        >
                            {loading ? 'Connecting...' : `Connect ${cfg.name}`}
                        </Button>
                    ) : (
                        <Tooltip title="This will remove the calendar sync for this provider" arrow>
                            <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                onClick={handleDisconnect}
                                disabled={disconnecting}
                                startIcon={disconnecting ? <CircularProgress size={14} color="inherit" /> : <LinkOffIcon />}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-1px)' } }}
                            >
                                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                            </Button>
                        </Tooltip>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default CalendarConnectionCard;
